/* Componentes que o MDX do guia pode usar sem importar.
 *
 * <Stat> é o mecanismo anti-obsolescência: os números do guia deixam de ser
 * prosa chumbada e passam a vir de /api/guide/metrics. Se uma chave sumir do
 * gerador, aparece um marcador vermelho na página em vez de "undefined". */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { intBR, pct } from "../lib/format";

interface Metric { value: number; unit: string; note: string }

export function useGuideMetrics() {
  return useQuery({
    queryKey: ["guide-metrics"],
    queryFn: () => api<{ generated_at: string; metrics: Record<string, Metric> }>(
      "/api/guide/metrics"),
    staleTime: 5 * 60_000,
  });
}

export function Stat({ k, as }: { k: string; as?: "pct" | "int" }) {
  const { data, isPending } = useGuideMetrics();
  if (isPending) {
    return <span className="skeleton" style={{ display: "inline-block", width: 44,
                                               height: "1em", verticalAlign: "-2px" }} />;
  }
  const metric = data?.metrics[k];
  if (!metric) {
    return <span className="tag alta" title={`métrica ausente: ${k}`}>?{k}</span>;
  }
  const text = as === "pct" ? `${metric.value}%`
             : as === "int" ? intBR(metric.value)
             : metric.unit === "%" ? `${metric.value}%`
             : intBR(metric.value);
  return (
    <span className="stat-inline" title={metric.note || undefined}>{text}</span>
  );
}

export function PurityTable() {
  const { data } = useQuery({
    queryKey: ["purity"],
    queryFn: () => api<{ type_key: string; label: string; entities: number;
                         median_purity: number | null; median_categories: number | null;
                         band: string }[]>("/api/ontology/purity"),
  });
  if (!data) return <div className="skeleton" style={{ height: 240 }} />;

  const bands = [
    ["identidade", "Identidade — dizem o que foi a transação"],
    ["contexto", "Contexto — dizem como ou onde, nunca o quê"],
  ];

  return (
    <div style={{ overflowX: "auto", margin: "16px 0" }}>
      <table>
        <thead>
          <tr>
            <th scope="col">tipo de entidade</th>
            <th scope="col" className="num">entidades</th>
            <th scope="col" className="num">pureza mediana</th>
            <th scope="col" className="num">categorias</th>
          </tr>
        </thead>
        <tbody>
          {bands.map(([band, title]) => (
            <>
              <tr key={band}>
                <td colSpan={4} style={{ background: "var(--surface-2)", fontWeight: 600,
                                         fontSize: 12 }}>{title}</td>
              </tr>
              {data.filter((r) => r.band === band).map((row) => (
                <tr key={row.type_key}>
                  <td>{row.label}</td>
                  <td className="num">{intBR(row.entities)}</td>
                  <td className="num">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <span style={{ width: 54, height: 8, background: "var(--surface-2)",
                                     borderRadius: 2, overflow: "hidden" }}>
                        <span style={{ display: "block", height: "100%",
                                       width: `${(row.median_purity ?? 0) * 100}%`,
                                       background: "var(--accent)" }} />
                      </span>
                      {pct(row.median_purity, 0)}
                    </span>
                  </td>
                  <td className="num">{row.median_categories}</td>
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* O diagrama desenhado à mão do guia original, portado como está. Usa
 * currentColor, então acompanha o tema sozinho. */
export function DiagramaLinhaGrafo() {
  return (
    <svg viewBox="0 0 900 264" width="100%" role="img" style={{ margin: "16px 0" }}
         aria-label="Uma linha do CSV — descrição ACAI DA CELIA, categoria DESPESA - COMER FORA, valor 14,17 — vira um grafo de três nós: a empresa ACAI DA CELIA, o setor ALIMENTACAO e a categoria, ligados por três arestas.">
      <defs>
        <marker id="seta" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" opacity=".45" />
        </marker>
      </defs>
      <g fontFamily="var(--font-mono)" fontSize="11.5" fill="currentColor">
        <rect x="8" y="30" width="330" height="96" rx="8" fill="none"
              stroke="currentColor" opacity=".25" />
        <text x="24" y="56" opacity=".55">description</text>
        <text x="132" y="56">ACAI DA CELIA</text>
        <text x="24" y="80" opacity=".55">CLASSIFIER_NEW</text>
        <text x="132" y="80">DESPESA - COMER FORA</text>
        <text x="24" y="104" opacity=".55">amount_total</text>
        <text x="132" y="104">14,17</text>
        <line x1="352" y1="78" x2="410" y2="78" stroke="currentColor" opacity=".45"
              markerEnd="url(#seta)" />
        <circle cx="500" cy="52" r="30" fill="none" stroke="#2a78d6" strokeWidth="1.6" />
        <text x="500" y="56" textAnchor="middle" fontSize="10">empresa</text>
        <rect x="452" y="140" width="96" height="40" rx="5" fill="none"
              stroke="currentColor" opacity=".55" />
        <text x="500" y="164" textAnchor="middle" fontSize="10">setor</text>
        <rect x="690" y="78" width="150" height="40" rx="20" fill="none"
              stroke="currentColor" opacity=".55" />
        <text x="765" y="102" textAnchor="middle" fontSize="10">categoria</text>
        <line x1="500" y1="82" x2="500" y2="140" stroke="currentColor" opacity=".3" />
        <line x1="530" y1="62" x2="690" y2="92" stroke="currentColor" opacity=".3" />
        <line x1="548" y1="156" x2="690" y2="110" stroke="currentColor" opacity=".3" />
        <text x="560" y="44" fontSize="10" opacity=".6">ACAI DA CELIA</text>
        <text x="560" y="196" fontSize="10" opacity=".6">ALIMENTACAO</text>
      </g>
    </svg>
  );
}

export const components = { Stat, PurityTable, DiagramaLinhaGrafo };
export default function useMDXComponents() { return components; }
